// User settings management
import { supabase } from "@/integrations/supabase/client";
import { getScoped, setScoped, removeScoped } from "./userScopedStorage";

export type HelpAlertType = "voice" | "siren" | "beep" | "train";

export interface ExternalSwitchMapping {
  key: string;
  code: string;
}

export interface UserSettings {
  name: string;
  gender: "male" | "female" | "neutral";
  voiceGender: "male" | "female";
  language: string;
  fontSize: "small" | "medium" | "large";
  theme: "light" | "dark" | "auto";
  isMuted: boolean;
  speechRate: number;
  onboardingComplete: boolean;
  controlMethod: string;
  // Control method specific settings
  eyeControlGestures: string[];
  mouthGestures: string[];
  mouthGestureActions: Record<string, string>; // gesture -> action mapping
  externalSwitchCount: number;
  // External switch settings
  externalSwitchScanType: "automatic" | "manual";
  externalSwitchMappings: Record<string, ExternalSwitchMapping>;
  // Scanning settings
  scanMode: "cell_by_cell" | "row_and_column";
  scanSpeed: number; // in seconds
  scanControlMode: "automatic" | "gesture_controlled";
  // New scanning settings (SRS additions)
  dwellTime: number; // in seconds (0.5 - 3.0)
  firstItemDelay: "off" | "normal" | "long"; // off=1x, normal=1.5x, long=2x
  // Action mapping
  actionMapping: Record<string, string>;
  // Help alert type
  helpAlertType: HelpAlertType;
  // Cloned voice
  clonedVoiceId: string | null;
  useClonedVoice: boolean;
  // Speech language override (defaults to user language)
  speechLanguage: string | null;
  // Night monitoring (EyeControl only)
  nightMonitoringEnabled: boolean;
  nightMonitoringStartTime: string; // HH:MM format
  nightMonitoringEndTime: string; // HH:MM format
  nightMonitoringDuration: number; // minutes (2-30)
  caregiverEmail: string | null;
  // Head tracking: movement amplification (1=small amplification, 10=large - less head movement needed)
  headTrackingSensitivity: number;
  // Gaze tracking: cursor smoothing (1=responsive but jittery, 10=smooth but slower)
  gazeTrackingSmoothing: number;
  // New tracking settings (SRS additions)
  cursorMagnetism: "off" | "weak" | "strong";
  cursorSmoothing: "low" | "medium" | "high";
  // Hierarchical scanning: max cycles at cell level before auto-return to row level
  cellLevelMaxCycles: number;
}

const DEFAULT_SETTINGS: UserSettings = {
  name: "",
  gender: "neutral",
  voiceGender: "female",
  language: "he",
  fontSize: "medium",
  theme: "dark",
  isMuted: false,
  speechRate: 0.9,
  onboardingComplete: false,
  controlMethod: "standard_pointer",
  eyeControlGestures: [],
  mouthGestures: [],
  mouthGestureActions: {},
  externalSwitchCount: 1,
  externalSwitchScanType: "automatic",
  externalSwitchMappings: {},
  scanMode: "cell_by_cell",
  scanSpeed: 3.0,
  scanControlMode: "automatic",
  // New scanning settings defaults
  dwellTime: 1.25, // 1.25 seconds default dwell
  firstItemDelay: "off", // disabled — feature removed from UI
  actionMapping: {},
  helpAlertType: "voice",
  clonedVoiceId: null,
  useClonedVoice: false,
  speechLanguage: null, // null = use user language
  // Night monitoring defaults
  nightMonitoringEnabled: false,
  nightMonitoringStartTime: "22:00",
  nightMonitoringEndTime: "06:00",
  nightMonitoringDuration: 5,
  caregiverEmail: null,
  // Head tracking sensitivity default (7 = high amplification for minimal movement)
  headTrackingSensitivity: 7,
  // Gaze tracking smoothing default (5 = balanced between responsive and smooth)
  gazeTrackingSmoothing: 5,
  // New tracking settings defaults
  cursorMagnetism: "strong", // default to strong magnetism for accessibility
  cursorSmoothing: "medium", // balanced smoothing
  // Hierarchical scanning: 2 full cycles through cells before auto-return to row level
  cellLevelMaxCycles: 2,
};

// Base keys — namespaced per-user via userScopedStorage helpers.
const STORAGE_KEY = "ayuni_user_settings";
const LOCAL_WRITE_TS_KEY = "ayuni_user_settings_local_ts";

/**
 * Returns the millisecond timestamp of the last local saveUserSettings call,
 * or 0 if none has been recorded. Used by the cloud→local merge to avoid
 * overwriting a fresh local change with a stale cloud value (race after reload).
 */
export function getLastLocalSettingsWriteTs(): number {
  try {
    const v = getScoped(LOCAL_WRITE_TS_KEY);
    return v ? parseInt(v, 10) || 0 : 0;
  } catch {
    return 0;
  }
}

export function getUserSettings(): UserSettings {
  try {
    const stored = getScoped(STORAGE_KEY);
    if (stored) {
      // Force firstItemDelay to "off" for all users (feature removed from UI)
      return { ...DEFAULT_SETTINGS, ...JSON.parse(stored), firstItemDelay: "off" };
    }
  } catch (e) {
    console.error("Failed to load user settings:", e);
  }
  return { ...DEFAULT_SETTINGS, firstItemDelay: "off" };
}

// Save settings to Supabase (async, fire-and-forget)
async function saveToSupabase(settings: Partial<UserSettings>): Promise<void> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;

    const dbSettings: Record<string, any> = {};
    
    // Map only the fields that were updated
    if (settings.name !== undefined) dbSettings.name = settings.name;
    if (settings.gender !== undefined) dbSettings.gender = settings.gender;
    if (settings.voiceGender !== undefined) dbSettings.voice_gender = settings.voiceGender;
    if (settings.language !== undefined) dbSettings.language = settings.language;
    if (settings.theme !== undefined) dbSettings.theme = settings.theme;
    if (settings.isMuted !== undefined) dbSettings.is_muted = settings.isMuted;
    if (settings.speechRate !== undefined) dbSettings.speech_rate = settings.speechRate;
    if (settings.controlMethod !== undefined) dbSettings.control_method = settings.controlMethod;
    if (settings.scanMode !== undefined) dbSettings.scan_mode = settings.scanMode;
    if (settings.scanSpeed !== undefined) dbSettings.scan_speed = Math.round(settings.scanSpeed * 1000);
    if (settings.externalSwitchScanType !== undefined) dbSettings.scan_type = settings.externalSwitchScanType;
    if (settings.externalSwitchCount !== undefined) dbSettings.external_switch_count = settings.externalSwitchCount;
    if (settings.externalSwitchMappings !== undefined) {
      dbSettings.external_switch_mappings = Object.values(settings.externalSwitchMappings);
    }
    if (settings.helpAlertType !== undefined) dbSettings.help_alert_type = settings.helpAlertType;
    if (settings.clonedVoiceId !== undefined) dbSettings.cloned_voice_id = settings.clonedVoiceId;
    if (settings.useClonedVoice !== undefined) dbSettings.use_cloned_voice = settings.useClonedVoice;
    if (settings.nightMonitoringEnabled !== undefined) dbSettings.night_monitoring_enabled = settings.nightMonitoringEnabled;
    if (settings.nightMonitoringStartTime !== undefined) dbSettings.night_monitoring_start_time = settings.nightMonitoringStartTime;
    if (settings.nightMonitoringEndTime !== undefined) dbSettings.night_monitoring_end_time = settings.nightMonitoringEndTime;
    if (settings.nightMonitoringDuration !== undefined) dbSettings.night_monitoring_duration = settings.nightMonitoringDuration;
    if (settings.caregiverEmail !== undefined) dbSettings.caregiver_email = settings.caregiverEmail;

    if (Object.keys(dbSettings).length === 0) return;

    const { error } = await supabase
      .from("user_settings")
      .upsert({ 
        user_id: session.user.id,
        ...dbSettings 
      }, { 
        onConflict: 'user_id' 
      });

    if (error) {
      console.error("[userSettings] Failed to save to Supabase:", error);
      // Handle FK constraint error (23503) - user doesn't exist in auth.users
      // This means the session is stale/invalid, sign out
      if (error.code === '23503') {
        console.warn("[userSettings] User not found in auth.users, signing out stale session");
        await supabase.auth.signOut();
      }
    }
  } catch (e) {
    console.error("[userSettings] Error saving to Supabase:", e);
  }
}

export function saveUserSettings(settings: Partial<UserSettings>): UserSettings {
  const current = getUserSettings();
  const updated = { ...current, ...settings };
  try {
    setScoped(STORAGE_KEY, JSON.stringify(updated));
    // Mark when local was last written, so post-reload cloud sync can avoid
    // clobbering a fresh local change with a stale server row.
    setScoped(LOCAL_WRITE_TS_KEY, String(Date.now()));
    // Notify app about settings changes (same-tab reactive updates)
    window.dispatchEvent(new CustomEvent('userSettingsChanged', {
      detail: { changedKeys: Object.keys(settings) }
    }));

    // If controlMethod changed, update sessionStorage and dispatch event
    if (settings.controlMethod) {
      sessionStorage.setItem('controlMethod', settings.controlMethod);
      // Also update the dedicated input method storage key (per-user namespaced)
      setScoped('ayuni_input_method', settings.controlMethod);
      // Dispatch event to notify all listeners immediately
      window.dispatchEvent(new CustomEvent('controlMethodChanged', {
        detail: { method: settings.controlMethod }
      }));
    }

    // Save to Supabase (fire-and-forget)
    saveToSupabase(settings);
  } catch (e) {
    console.error("Failed to save user settings:", e);
  }
  return updated;
}

/**
 * Same as saveUserSettings, but awaits the cloud write so callers (e.g. the
 * Settings page when changing language right before window.location.reload())
 * can ensure the server has the new value before the page reloads.
 */
export async function saveUserSettingsAsync(settings: Partial<UserSettings>): Promise<UserSettings> {
  const updated = saveUserSettings(settings);
  try {
    await saveToSupabase(settings);
  } catch (e) {
    console.error("Failed to sync settings to cloud:", e);
  }
  return updated;
}

export function resetUserSettings(): UserSettings {
  removeScoped(STORAGE_KEY);
  return DEFAULT_SETTINGS;
}

// Gender-aware greeting helper
export function getGreeting(settings: UserSettings): string {
  const hour = new Date().getHours();
  const name = settings.name || "אורח";

  let timeGreeting: string;
  if (hour >= 5 && hour < 12) {
    timeGreeting = settings.gender === "female" ? "בוקר טוב" : "בוקר טוב";
  } else if (hour >= 12 && hour < 17) {
    timeGreeting = settings.gender === "female" ? "צהריים טובים" : "צהריים טובים";
  } else if (hour >= 17 && hour < 21) {
    timeGreeting = settings.gender === "female" ? "ערב טוב" : "ערב טוב";
  } else {
    timeGreeting = settings.gender === "female" ? "לילה טוב" : "לילה טוב";
  }

  return `${timeGreeting}, ${name}`;
}

// Gender-aware question helper
export function getWhatToDo(settings: UserSettings): string {
  if (settings.gender === "female") {
    return "מה תרצי לעשות היום?";
  } else if (settings.gender === "male") {
    return "מה תרצה לעשות היום?";
  }
  return "מה נעשה היום?";
}
